import axios from "axios";
import { toast } from 'react-toastify';

const BASE_URL = 'http://154.127.86.92:8000';


// Fonction pour vérifier si un token JWT est expiré
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Erreur lors de la vérification du token:', error);
    return true;
  }
};

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    timeout: 0  // Pas de timeout pour les longues importations
});

// Fonction pour rafraîchir le token
const refreshToken = async (refreshToken) => {
    try {
        console.log('Attempting to refresh token...');
        const response = await axios.post(`${BASE_URL}/token/refresh/`, {
            refresh: refreshToken
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        if (response.data.access) {
            console.log('Token refresh successful');
            const tokensString = localStorage.getItem('auth_tokens');
            if (tokensString) {
                const tokens = JSON.parse(tokensString);
                const newTokens = {
                    ...tokens,
                    access: response.data.access,
                    refresh: response.data.refresh || tokens.refresh
                };
                localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
                
                // Mettre à jour le state global si disponible - simplifier
                if (window.useGlobalStore) {
                    const globalState = window.useGlobalStore.getState();
                    globalState.setAuthLoading(false);
                }
                
                return response.data.access;
            }
        }
        return null;
    } catch (error) {
        console.error('Erreur lors du rafraîchissement du token:', error);
        if (error.response) {
            console.error('Status:', error.response.status);
            console.error('Data:', error.response.data);
        }
        localStorage.removeItem('auth_tokens');
        
        // Mettre à jour le state global si disponible
        if (window.useGlobalStore) {
            window.useGlobalStore.getState().setAuthLoading(false);
        }
        
        return null;
    }
};

// Vérifier si l'URL actuelle est la page de connexion ou d'inscription
const isAuthPage = () => {
    const path = window.location.pathname;
    return path === '/connexion' || path.includes('/inscription');
};

// Fonction pour gérer la déconnexion
const handleLogout = (showToast = true) => {
    console.log('Handling logout...');
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('credentials');
    
    // Mettre à jour le state global si disponible
    if (window.useGlobalStore) {
        window.useGlobalStore.getState().logout();
    }
    
    // Ne pas rediriger ici - laisser React Router gérer cela via PrivateRoute
    if (showToast && !isAuthPage()) {
        toast.error("Votre session a expiré. Veuillez vous reconnecter.");
    }
};

// Intercepteur pour ajouter le token aux requêtes
api.interceptors.request.use(
    async (config) => {
        // Ne pas ajouter de token pour les routes d'authentification
        if (config.url.includes('/utilisateur/signin/') || 
            config.url.includes('/utilisateur/register/') || 
            config.url.includes('/utilisateur/login/') || 
            config.url.includes('/token/')) {
            return config;
        }
        
        try {
            const tokensString = localStorage.getItem('auth_tokens');
            if (tokensString) {
                const tokens = JSON.parse(tokensString);
                
                // Si le token d'accès est expiré, on essaie de le rafraîchir
                if (isTokenExpired(tokens.access)) {
                    console.log('Access token expired, attempting refresh...');
                    
                    const newAccessToken = await refreshToken(tokens.refresh);
                    if (newAccessToken) {
                        config.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    } else {
                        // Si le rafraîchissement échoue, on déconnecte l'utilisateur
                        handleLogout();
                        return Promise.reject(new Error('Token refresh failed'));
                    }
                } else {
                    config.headers['Authorization'] = `Bearer ${tokens.access}`;
                }
            }
            return config;
        } catch (error) {
            console.error('Error in request interceptor:', error);
            return Promise.reject(error);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
    (response) => {
        // Ne mettre à jour l'utilisateur QUE pour les endpoints d'auth
        if (response?.config?.url) {
            const url = response.config.url;
            const isAuthEndpoint = url.includes('/utilisateur/signin/') ||
                                   url.includes('/utilisateur/register/') ||
                                   url.includes('/utilisateur/login/');
            if (isAuthEndpoint && response.data && response.data.user && window.useGlobalStore) {
                const { updateUserInfo } = window.useGlobalStore.getState();
                updateUserInfo(response.data.user);
            }
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Si c'est une requête d'authentification (login ou register), ne pas traiter l'erreur ici
        // et laisser le composant la gérer
        if (originalRequest.url.includes('/utilisateur/signin/') || 
            originalRequest.url.includes('/utilisateur/register/') ||
            originalRequest.url.includes('/utilisateur/login/')) {
            return Promise.reject(error);
        }
        
        // Vérifier si c'est une erreur d'authentification (401)
        if (error.response?.status === 401) {
            console.log('401 error received, handling logout...');
            // Si c'est une erreur de token expiré et que la requête n'a pas déjà été retentée
            if (!originalRequest._retry) {
                originalRequest._retry = true;
                
                try {
                    const tokensString = localStorage.getItem('auth_tokens');
                    if (tokensString) {
                        const tokens = JSON.parse(tokensString);
                        const newAccessToken = await refreshToken(tokens.refresh);
                        
                        if (newAccessToken) {
                            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                            return api(originalRequest);
                        }
                    }
                } catch (refreshError) {
                    console.error('Erreur lors du rafraîchissement du token:', refreshError);
                }
                
                // Si le rafraîchissement échoue, on déconnecte l'utilisateur
                handleLogout();
            }
        }
        
        return Promise.reject(error);
    }
);

export default api;